import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  configurationReducers,
  configurationSlice,
  type ConfigurationState,
} from "@/features/configuration/slice";
import definition from "@/legacy/store/definition";

export type LegacyState = Record<string, unknown>;
type LegacyMutation = (state: LegacyState, payload: unknown) => void;
type LegacyCaseReducer = (state: LegacyState, action: PayloadAction<unknown>) => void;

const mutations = definition.mutations as Record<string, LegacyMutation>;

// Ogni mutation Vuex diventa un case reducer con lo stesso nome: Immer permette la scrittura in place.
const reducers: Record<string, LegacyCaseReducer> = Object.fromEntries(
  Object.entries(mutations).map(([name, mutation]) => [
    name,
    (state, action) => {
      mutation(state, action.payload);
    },
  ]),
);

const asConfiguration = (state: LegacyState) => state as unknown as ConfigurationState;
const { actions: configurationActions } = configurationSlice;

export const legacySlice = createSlice({
  name: "legacy",
  initialState: definition.state as LegacyState,
  reducers,
  // Lo slice `configuration` (spec 5.3) governa currentConfiguration/historyChanges/websiteAttributes, che oggi
  // vivono qui perche' le viste legacy li leggono ancora. In fase 8 si monta configurationSlice.reducer e questo blocco sparisce.
  extraReducers: (builder) => {
    builder
      .addCase(configurationActions.startEditing, (state, action) =>
        configurationReducers.startEditing(asConfiguration(state), action),
      )
      .addCase(configurationActions.replaceConfiguration, (state, action) =>
        configurationReducers.replaceConfiguration(asConfiguration(state), action),
      )
      .addCase(configurationActions.updateField, (state, action) =>
        configurationReducers.updateField(asConfiguration(state), action),
      )
      .addCase(configurationActions.updateFields, (state, action) =>
        configurationReducers.updateFields(asConfiguration(state), action),
      )
      .addCase(configurationActions.unsetField, (state, action) =>
        configurationReducers.unsetField(asConfiguration(state), action),
      )
      .addCase(configurationActions.patchField, (state, action) =>
        configurationReducers.patchField(asConfiguration(state), action),
      )
      .addCase(configurationActions.undo, (state) => configurationReducers.undo(asConfiguration(state)))
      .addCase(configurationActions.markSaved, (state) =>
        configurationReducers.markSaved(asConfiguration(state)),
      )
      .addCase(configurationActions.setWebsiteAttributes, (state, action) =>
        configurationReducers.setWebsiteAttributes(asConfiguration(state), action),
      );
  },
});

export type LegacyActionCreator<P = unknown> = (payload: P) => PayloadAction<P>;

/**
 * Creator tipizzato di una mutation legacy. Lo slice e' costruito dinamicamente dalla mappa delle
 * mutation Vuex, quindi con noUncheckedIndexedAccess `legacySlice.actions[name]` e' "possibly undefined":
 * qui si controlla una volta sola, a runtime, e si dichiara il tipo del payload.
 */
export function legacyAction<P = unknown>(name: string): LegacyActionCreator<P> {
  const creator = legacySlice.actions[name];
  if (!creator) throw new Error(`Mutation legacy sconosciuta: ${name}`);
  return creator as unknown as LegacyActionCreator<P>;
}
